import { invasionPolicies } from '../data/world/invasionPolicies'
import { nameKey } from './worldFormat'
import type { Bag, BagReward, WorldData } from '../types/world'

/*
 * Bolsas de recompensa (16_bags → 17_bag_triggers / 34_event_bags → 19_bag_sections → 21_bag_rewards).
 * Una bolsa es una LISTA DE RESULTADOS POSIBLES, no un paquete que se entrega completo
 * (salvo secciones con give_all = true). Solo se presenta como "caja que abres" cuando
 * 17_bag_triggers indica un ítem que la activa; si la activa un monstruo, es una recompensa
 * interna al derrotarlo.
 */

export type BagOrigin =
  | { kind: 'open'; itemKey: string; itemName: string }
  | { kind: 'monster'; monsterId: number }
  | { kind: 'event'; eventId: string; eventName: string }

export type BagSource = {
  bag: Bag
  bagName: string
  origins: BagOrigin[]
  /** Nº de resultados posibles en el mismo grupo. */
  poolSize?: number
  giveAll: boolean
  levelMin?: number
  levelMax?: number
  /** Retenida por política de invasión (conflicto o falta de lista vigente). */
  withheldReason?: string
}

function itemKeyFor(w: WorldData, itemId: string, level?: number): string {
  const v = level !== undefined ? `${itemId}@${level}` : undefined
  return v && w.variants.has(v) ? v : itemId
}

export function itemDisplayName(w: WorldData, key: string): string {
  return w.variants.get(key)?.name ?? w.items.get(key)?.name ?? key
}

export function bagName(w: WorldData, bag: Bag): string {
  if (bag.name) return bag.name
  const opener = (w.idx.bagTriggersByBag.get(bag.id) ?? []).find((t) => t.itemId)
  return opener?.itemId ? itemDisplayName(w, itemKeyFor(w, opener.itemId, opener.itemLevel)) : `Bolsa ${bag.id}`
}

/** Origen de la bolsa: ítem que se abre, monstruo que la entrega o evento. */
export function bagOrigins(w: WorldData, bagId: string): BagOrigin[] {
  const out: BagOrigin[] = []
  ;(w.idx.bagTriggersByBag.get(bagId) ?? []).forEach((t) => {
    if (t.usedByRuntime === false) return
    if (t.itemId) {
      const key = itemKeyFor(w, t.itemId, t.itemLevel)
      out.push({ kind: 'open', itemKey: key, itemName: itemDisplayName(w, key) })
    } else if (t.monsterId !== undefined) out.push({ kind: 'monster', monsterId: t.monsterId })
  })
  ;(w.idx.eventBagsByBag.get(bagId) ?? []).forEach((e) => {
    if (e.active === false) return
    if (!out.some((o) => o.kind === 'event' && o.eventId === e.eventId)) out.push({ kind: 'event', eventId: e.eventId, eventName: w.events.get(e.eventId)?.name ?? e.eventId })
  })
  return out
}

function invasionHold(w: WorldData, bag: Bag, origins: BagOrigin[], rewardName: string, itemId?: string): string | undefined {
  const policy = invasionPolicies.find(
    (p) =>
      p.bagIds.includes(bag.id) ||
      origins.some((o) => (o.kind === 'monster' && p.monsterIds.includes(o.monsterId)) || (o.kind === 'event' && p.eventIds.includes(o.eventId))),
  )
  if (!policy) return undefined
  if (itemId && policy.authorizedItemIds?.includes(itemId)) return undefined
  if (policy.authorizedNames.some((n) => nameKey(n) === nameKey(rewardName))) return undefined
  return `${policy.name}: en revisión. ${policy.missing ?? 'El ítem no figura de forma expresa en la información aprobada.'}`
}

function sourceFor(w: WorldData, r: BagReward): BagSource | undefined {
  const bag = w.bags.get(r.bagId)
  if (!bag || bag.usedByRuntime === false || r.usedByRuntime === false) return undefined
  const sections = r.poolId ? w.idx.bagSectionsByPool.get(r.poolId) ?? [] : []
  if (sections.length && sections.every((s) => s.usedByRuntime === false)) return undefined
  const origins = bagOrigins(w, bag.id)
  const rv = r.variantId ? w.bagRewardVariants.get(r.variantId) : undefined
  const pool = r.poolId ? w.idx.bagRewardsByPool.get(r.poolId) ?? [] : []
  const rewardName = r.itemId ? w.items.get(r.itemId)?.name ?? r.itemId : ''
  return {
    bag,
    bagName: bagName(w, bag),
    origins,
    poolSize: pool.filter((p) => p.usedByRuntime !== false).length || undefined,
    giveAll: sections.some((s) => s.giveAll === true),
    levelMin: rv?.levelMin,
    levelMax: rv?.levelMax,
    withheldReason: invasionHold(w, bag, origins, rewardName, r.itemId),
  }
}

/** Todas las bolsas que contienen el ítem (vista técnica, sin filtrar). */
export function allBagSources(w: WorldData, itemKey: string): BagSource[] {
  const seen = new Set<string>()
  const out: BagSource[] = []
  ;(w.idx.bagRewardsByItem.get(itemKey) ?? []).forEach((r) => {
    const s = sourceFor(w, r)
    if (!s) return
    const k = `${s.bag.id}|${s.levelMin ?? ''}|${s.levelMax ?? ''}`
    if (seen.has(k)) return
    seen.add(k)
    out.push(s)
  })
  return out
}

/**
 * Bolsas visibles para el jugador: todas las que tienen un origen documentado (caja que se abre,
 * monstruo que la entrega o evento), salvo las retenidas por conflicto entre fuentes.
 */
export function playerBagSources(w: WorldData, itemKey: string): BagSource[] {
  return allBagSources(w, itemKey).filter((s) => !s.withheldReason && s.origins.length > 0)
}

/** ¿La bolsa es una caja que abre el jugador? Si no, es una lista interna de resultados (jefe o evento). */
export const isOpenableBag = (s: BagSource) => s.origins.some((o) => o.kind === 'open')

export type BagContent = { itemKey: string; name: string; levelMin?: number; levelMax?: number; withheld?: string }

/** Contenido de una bolsa. Los resultados retenidos por conflicto se marcan, no se presentan como confirmados. */
export function bagContents(w: WorldData, bagId: string): BagContent[] {
  const seen = new Set<string>()
  const bag = w.bags.get(bagId)
  const origins = bag ? bagOrigins(w, bagId) : []
  return w.bagRewards
    .filter((r) => r.bagId === bagId && r.usedByRuntime !== false && r.itemId)
    .map((r) => {
      const rv = r.variantId ? w.bagRewardVariants.get(r.variantId) : undefined
      const name = w.items.get(r.itemId!)?.name ?? r.itemId!
      return { itemKey: r.itemId!, name, levelMin: rv?.levelMin, levelMax: rv?.levelMax, withheld: bag ? invasionHold(w, bag, origins, name, r.itemId) : undefined }
    })
    .filter((x) => {
      const k = `${x.itemKey}|${x.levelMin ?? ''}|${x.levelMax ?? ''}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
}

/** Bolsas que entrega un monstruo al ser derrotado (17_bag_triggers o 34_event_bags). */
export function monsterBags(w: WorldData, monsterId: number): Bag[] {
  const ids = new Set<string>([
    ...w.bagTriggers.filter((t) => t.monsterId === monsterId && t.usedByRuntime !== false).map((t) => t.bagId),
    ...w.eventBags.filter((e) => e.monsterId === monsterId && e.active !== false).map((e) => e.bagId),
  ])
  return Array.from(ids).map((id) => w.bags.get(id)).filter((b): b is Bag => !!b && b.usedByRuntime !== false)
}

/** Bolsas que se abren con este ítem (cajas). */
export function bagsOpenedBy(w: WorldData, itemKey: string): Bag[] {
  const base = w.variants.get(itemKey)?.itemId ?? itemKey
  const level = w.variants.get(itemKey)?.level
  const ids = new Set(
    w.bagTriggers
      .filter((t) => t.usedByRuntime !== false && t.itemId === base && (level === undefined || t.itemLevel === undefined || t.itemLevel === level))
      .map((t) => t.bagId),
  )
  return Array.from(ids).map((id) => w.bags.get(id)).filter((b): b is Bag => !!b && b.usedByRuntime !== false)
}

export const levelRange = (min?: number, max?: number) =>
  min === undefined && max === undefined ? undefined : min === max || max === undefined ? `+${min}` : min === undefined ? `hasta +${max}` : `+${min} a +${max}`
